import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Star, Check, X, Sparkles, Trash2, Loader2, BookMarked,
  RefreshCw, PenLine, Quote,
} from "lucide-react";
import "./shelf.css";

/**
 * BookShelf. The person's saved books standing on a wooden shelf.
 * Clicking a spine opens a detail card with an AI synopsis, AI
 * paraphrased ideas, a save-as-favourite toggle, mark-as-read, and a
 * space to write their own favourite quote from the book.
 *
 * All writes go back through the callbacks so the parent stays the
 * single source of truth for the rail.
 */

const SPINES = [
  "#5B6BD6", "#0E9E7E", "#E8874B", "#C0497B", "#6D4AE0",
  "#2F9BD4", "#C99A22", "#417D5A", "#B5533E", "#4A6FA5",
];

function spineColor(title) {
  let h = 0;
  const s = title || "";
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return SPINES[h % SPINES.length];
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "reading", label: "Reading" },
  { key: "want_to_read", label: "Want to read" },
  { key: "read", label: "Read" },
  { key: "favorite", label: "Favourites" },
];

// How many covers fit on one plank, from the container width. Keeps the
// covers a fixed size and reflows the rows so a plank is never crowded or
// overflowing, on any screen.
function useCols(ref, pitch = 104, min = 2, max = 8) {
  const [cols, setCols] = useState(4);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const compute = () => {
      const w = el.clientWidth || 0;
      const c = Math.floor((w + 12) / pitch);
      setCols(Math.max(min, Math.min(max, c || min)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, pitch, min, max]);
  return cols;
}

export default function BookShelf({
  entries,
  onSetState,
  onToggleFavorite,
  onSaveQuote,
  onGenerateSynopsis,
  onRemove,
}) {
  const reduce = useReducedMotion();
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const wrapRef = useRef(null);
  const cols = useCols(wrapRef);

  const counts = useMemo(() => ({
    all: entries.length,
    reading: entries.filter((e) => e.state === "reading").length,
    want_to_read: entries.filter((e) => e.state === "want_to_read").length,
    read: entries.filter((e) => e.state === "read").length,
    favorite: entries.filter((e) => e.favorite).length,
  }), [entries]);

  const shown = useMemo(() => {
    if (filter === "all") return entries;
    if (filter === "favorite") return entries.filter((e) => e.favorite);
    return entries.filter((e) => e.state === filter);
  }, [entries, filter]);

  const rows = useMemo(() => {
    const out = [];
    for (let i = 0; i < shown.length; i += cols) out.push(shown.slice(i, i + cols));
    return out;
  }, [shown, cols]);

  const openEntry = useMemo(
    () => entries.find((e) => e.id === openId) || null,
    [entries, openId],
  );

  return (
    <div className="sh-wrap" ref={wrapRef} data-testid="my-shelf">
      <div className="sh-filters" data-testid="shelf-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className="sh-filter"
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`shelf-filter-${f.key}`}
          >
            {f.key === "favorite" && <Star size={12} fill={filter === f.key ? "currentColor" : "none"} />}
            {f.label}
            <span className="sh-count">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="sh-empty" data-testid="shelf-empty">
          <BookMarked size={30} className="mx-auto mb-3" style={{ color: "hsl(var(--klareet-muted))" }} />
          <p style={{ color: "hsl(var(--klareet-ink))", fontWeight: 600 }}>
            {filter === "all" ? "Your shelf is empty." : "Nothing here yet."}
          </p>
          <p className="k-quiet !text-sm mt-1">
            {filter === "all"
              ? "Save a book from the Discover tab and it lands here."
              : "Try another filter, or save more books from Discover."}
          </p>
        </div>
      ) : (
        rows.map((row, ri) => (
          <div className="sh-shelf" key={ri} data-testid={`shelf-row-${ri}`}>
            <div className="sh-books">
              {row.map((e, ci) => {
                const gi = ri * cols + ci;
                return (
                  <motion.button
                    key={e.id}
                    className="sh-cover"
                    style={{ "--spine": spineColor(e.title) }}
                    initial={reduce ? false : { y: -24, opacity: 0, rotate: -4 }}
                    animate={{ y: 0, opacity: 1, rotate: 0 }}
                    transition={reduce ? { duration: 0 } : { delay: gi * 0.045, type: "spring", stiffness: 300, damping: 22 }}
                    whileHover={reduce ? undefined : { y: -12 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setOpenId(e.id)}
                    aria-label={`Open ${e.title}`}
                    title={e.title}
                    data-testid={`shelf-book-${gi}`}
                  >
                    <span className="sh-cover-inner"><CoverArt entry={e} /></span>
                    {e.favorite && <Star size={16} className="sh-fav-c" fill="#F5B301" />}
                    {e.state === "read" && (
                      <span className="sh-read-c"><Check size={12} strokeWidth={3} /></span>
                    )}
                  </motion.button>
                );
              })}
            </div>
            <div className="sh-plank" />
            <div className="sh-plank-shadow" />
          </div>
        ))
      )}

      <AnimatePresence>
        {openEntry && (
          <BookDetailModal
            key={openEntry.id}
            entry={openEntry}
            reduce={reduce}
            onClose={() => setOpenId(null)}
            onSetState={onSetState}
            onToggleFavorite={onToggleFavorite}
            onSaveQuote={onSaveQuote}
            onGenerateSynopsis={onGenerateSynopsis}
            onRemove={async (id) => { await onRemove(id); setOpenId(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


// A book cover: the real image when there is one, otherwise a generated
// cover (title + author on the book's colour) so the shelf still reads as
// a shelf of books, never blank tiles.
function CoverArt({ entry }) {
  const [broken, setBroken] = useState(false);
  if (entry.cover_url && !broken) {
    return (
      <img
        src={entry.cover_url}
        alt=""
        className="sh-cover-img"
        loading="lazy"
        onError={() => setBroken(true)}
        data-testid="shelf-cover-img"
      />
    );
  }
  return (
    <span className="sh-gen" style={{ "--spine": spineColor(entry.title) }} data-testid="shelf-cover-gen">
      <span>
        <span className="sh-gen-top" />
        <span className="sh-gen-title">{entry.title}</span>
      </span>
      <span className="sh-gen-author">{entry.author}</span>
    </span>
  );
}


function ModalCover({ entry }) {
  const [broken, setBroken] = useState(false);
  if (entry.cover_url && !broken) {
    return (
      <img
        src={entry.cover_url}
        alt={entry.title}
        onError={() => setBroken(true)}
        className="sh-modal-cover"
        data-testid="shelf-detail-cover"
      />
    );
  }
  return (
    <div className="sh-modal-cover" style={{ overflow: "hidden", "--spine": spineColor(entry.title) }} data-testid="shelf-detail-cover-fallback">
      <span className="sh-gen" style={{ padding: "13px 12px 14px" }}>
        <span>
          <span className="sh-gen-top" />
          <span className="sh-gen-title" style={{ fontSize: 13, WebkitLineClamp: 5 }}>{entry.title}</span>
        </span>
        <span className="sh-gen-author">{entry.author}</span>
      </span>
    </div>
  );
}


function StateChip({ label, active, onClick, disabled, tid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition-colors"
      style={active
        ? { background: "hsl(var(--klareet-teal))", color: "white" }
        : { background: "hsl(var(--klareet-canvas-2))", color: "hsl(var(--klareet-ink-soft))", border: "1px solid hsl(var(--klareet-line-soft))" }}
      aria-pressed={active}
      data-testid={tid}
    >
      {label}
    </button>
  );
}


function BookDetailModal({
  entry, reduce, onClose,
  onSetState, onToggleFavorite, onSaveQuote, onGenerateSynopsis, onRemove,
}) {
  const [quote, setQuote] = useState(entry.favorite_quote || "");
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [quoteSaved, setQuoteSaved] = useState(false);
  const [gen, setGen] = useState("idle"); // idle | loading | done | failed | unavailable
  const [busyState, setBusyState] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    setQuote(entry.favorite_quote || "");
    setQuoteSaved(false);
  }, [entry.id, entry.favorite_quote]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const hasSynopsis = !!(entry.synopsis && entry.synopsis.trim());
  const ideas = entry.key_ideas || [];

  const runGenerate = async (refresh = false) => {
    setGen("loading");
    try {
      const data = await onGenerateSynopsis(entry.id, refresh);
      if (data && data.ai_available === false) setGen("unavailable");
      else if (data && data.generation_failed) setGen("failed");
      else setGen("done");
    } catch (_e) {
      setGen("failed");
    }
  };

  const saveQuote = async () => {
    setQuoteSaving(true); setQuoteSaved(false);
    try { await onSaveQuote(entry.id, quote); setQuoteSaved(true); }
    catch (_e) { /* soft */ }
    finally { setQuoteSaving(false); }
  };

  const changeState = async (s) => {
    setBusyState(true);
    try { await onSetState(entry.id, s); }
    finally { setBusyState(false); }
  };

  return (
    <motion.div
      className="k-session-overlay"
      style={{ animation: "none" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduce ? 0 : 0.18 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shelf-detail-title"
      data-testid="shelf-detail-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        className="k-session-card sh-modal-card"
        style={{ maxHeight: "88vh", overflowY: "auto" }}
        initial={reduce ? false : { scale: 0.94, y: 14, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={reduce ? { opacity: 0 } : { scale: 0.96, y: 8, opacity: 0 }}
        transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 320, damping: 26 }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-4 min-w-0">
            <ModalCover entry={entry} />
            <div className="min-w-0">
              <h2
                id="shelf-detail-title"
                style={{ fontSize: "1.3rem", lineHeight: 1.22 }}
                data-testid="shelf-detail-title"
              >
                {entry.title}
              </h2>
              {entry.author && <div className="k-quiet !text-sm mt-0.5">{entry.author}</div>}
              {entry.anchor && (
                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] mt-2" style={{ background: "hsl(var(--klareet-canvas-2))", color: "hsl(var(--klareet-muted))" }}>
                  {entry.anchor.replaceAll("_", " ")}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="k-btn k-btn-ghost !p-1.5 !px-1.5 -mr-1 -mt-1 shrink-0"
            aria-label="close"
            data-testid="shelf-detail-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Status + favourite */}
        <div className="sh-section" style={{ borderTop: "none", paddingTop: "1.1rem", marginTop: "1.1rem" }}>
          <div className="k-label mb-2">where it is</div>
          <div className="flex flex-wrap items-center gap-2">
            <StateChip label="Want to read" active={entry.state === "want_to_read"} disabled={busyState} onClick={() => changeState("want_to_read")} tid="shelf-detail-want" />
            <StateChip label="Reading" active={entry.state === "reading"} disabled={busyState} onClick={() => changeState("reading")} tid="shelf-detail-reading" />
            <StateChip label="Read" active={entry.state === "read"} disabled={busyState} onClick={() => changeState("read")} tid="shelf-detail-read" />
            <button
              onClick={() => onToggleFavorite(entry.id, !entry.favorite)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ml-auto"
              style={entry.favorite
                ? { background: "hsl(45 92% 52% / 0.16)", color: "hsl(38 80% 38%)", border: "1px solid hsl(45 92% 52% / 0.5)" }
                : { background: "hsl(var(--klareet-canvas-2))", color: "hsl(var(--klareet-ink-soft))", border: "1px solid hsl(var(--klareet-line-soft))" }}
              aria-pressed={entry.favorite}
              data-testid="shelf-detail-favorite"
            >
              <Star size={13} fill={entry.favorite ? "currentColor" : "none"} />
              {entry.favorite ? "Favourited" : "Favourite"}
            </button>
          </div>
        </div>

        {/* Synopsis */}
        <div className="sh-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="k-label !mb-0">synopsis</div>
            {hasSynopsis && (
              <span className="sh-ai-tag"><Sparkles size={11} /> AI summary</span>
            )}
          </div>

          {gen === "loading" ? (
            <div data-testid="shelf-synopsis-loading">
              <div className="sh-shimmer" style={{ width: "100%" }} />
              <div className="sh-shimmer" style={{ width: "92%" }} />
              <div className="sh-shimmer" style={{ width: "70%" }} />
            </div>
          ) : hasSynopsis ? (
            <div>
              <p className="klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.6, fontSize: "0.98rem" }} data-testid="shelf-synopsis-text">
                {entry.synopsis}
              </p>
              <button
                onClick={() => runGenerate(true)}
                className="k-btn k-btn-ghost !text-xs !py-1 !px-2 mt-2"
                data-testid="shelf-synopsis-refresh"
              >
                <RefreshCw size={12} /> Regenerate
              </button>
            </div>
          ) : gen === "unavailable" ? (
            <p className="k-quiet !text-sm" data-testid="shelf-synopsis-unavailable">
              AI summaries are not switched on for this workspace yet. Your own notes and quote below still work.
            </p>
          ) : gen === "failed" ? (
            <div data-testid="shelf-synopsis-failed">
              <p className="k-quiet !text-sm mb-2">That did not generate just now. Give it another try.</p>
              <button onClick={() => runGenerate(false)} className="k-btn k-btn-outline !text-xs !py-1.5 !px-3">
                <RefreshCw size={13} /> Try again
              </button>
            </div>
          ) : (
            <div>
              <p className="k-quiet !text-sm mb-3" style={{ lineHeight: 1.5 }}>
                Generate a short plain-language summary of what this book is about.
              </p>
              <button
                onClick={() => runGenerate(false)}
                className="k-btn k-btn-primary !text-sm"
                data-testid="shelf-synopsis-generate"
              >
                <Sparkles size={14} /> Generate synopsis
              </button>
            </div>
          )}
        </div>

        {/* AI paraphrased ideas */}
        {ideas.length > 0 && (
          <div className="sh-section" data-testid="shelf-ideas">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="k-label !mb-0">ideas from this book</div>
              <span className="sh-ai-tag"><Sparkles size={11} /> AI paraphrase</span>
            </div>
            <p className="k-quiet !text-xs mb-3" style={{ lineHeight: 1.5 }}>
              Written by AI in its own words, not exact quotes. Check the wording against the book before you quote it.
            </p>
            <ul className="space-y-2.5">
              {ideas.map((idea, i) => (
                <li key={i} className="sh-idea" data-testid={`shelf-idea-${i}`}>
                  <span className="sh-idea-num">{i + 1}</span>
                  <span style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5, fontSize: "0.92rem" }}>{idea}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* The person's own quote */}
        <div className="sh-section" data-testid="shelf-quote">
          <div className="k-label mb-1 flex items-center gap-1.5"><PenLine size={12} /> your favourite quote</div>
          <p className="k-quiet !text-xs mb-2" style={{ lineHeight: 1.5 }}>
            A line from the book that stuck with you, in your own hand. This one is yours, not AI.
          </p>
          {entry.favorite_quote && (
            <div className="sh-quote-box mb-3">
              <div className="klareet-serif" style={{ fontStyle: "italic", color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }}>
                <Quote size={13} style={{ display: "inline", marginRight: 4, color: "hsl(var(--klareet-amber))" }} />
                {entry.favorite_quote}
              </div>
            </div>
          )}
          <textarea
            className="k-input"
            style={{ minHeight: "4.5rem", resize: "vertical" }}
            maxLength={600}
            placeholder="Type the quote you want to keep."
            value={quote}
            onChange={(e) => { setQuote(e.target.value); setQuoteSaved(false); }}
            data-testid="shelf-quote-input"
          />
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={saveQuote}
              disabled={quoteSaving || quote === (entry.favorite_quote || "")}
              className="k-btn k-btn-outline !text-xs !py-1.5 !px-3"
              data-testid="shelf-quote-save"
            >
              {quoteSaving ? <Loader2 size={13} className="animate-spin" /> : null}
              {quoteSaving ? "Saving" : "Save quote"}
            </button>
            {quoteSaved && <span className="k-quiet !text-xs" style={{ color: "hsl(var(--klareet-teal))" }}>Saved.</span>}
          </div>
        </div>

        {/* Remove */}
        <div className="sh-section flex items-center justify-between gap-3">
          {!confirmRemove ? (
            <button
              onClick={() => setConfirmRemove(true)}
              className="k-btn k-btn-ghost !text-xs !py-1.5 !px-2"
              style={{ color: "hsl(var(--klareet-muted))" }}
              data-testid="shelf-remove"
            >
              <Trash2 size={13} /> Remove from shelf
            </button>
          ) : (
            <div className="flex items-center gap-2" data-testid="shelf-remove-confirm">
              <span className="k-quiet !text-xs">Remove this book?</span>
              <button onClick={() => onRemove(entry.id)} className="k-btn !text-xs !py-1.5 !px-3" style={{ background: "hsl(var(--destructive))", color: "white" }} data-testid="shelf-remove-yes">
                Remove
              </button>
              <button onClick={() => setConfirmRemove(false)} className="k-btn k-btn-ghost !text-xs !py-1.5 !px-2">
                Keep
              </button>
            </div>
          )}
          <button onClick={onClose} className="k-btn k-btn-ghost !text-sm shrink-0" data-testid="shelf-detail-done">
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
